import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, ArrowRight, UserPlus, LockKey, Bell, Megaphone, Receipt } from "@phosphor-icons/react";

const PAYMENT_STATUS_META = {
  al_dia: { color: "#32D74B", label: "Al día" },
  advertencia: { color: "#FFD60A", label: "Advertencia" },
  mora: { color: "#FF3B30", label: "En mora" },
  sin_pagos: { color: "#A3A3A3", label: "Sin pagos" },
};

const NOTIFICATION_TEMPLATES = [
  { name: "Recordatorio wellness lunes", type: "info", title: "¿Cómo te sentís esta semana?", body: "Completá tu cuestionario wellness de 30 segundos para mantener tu tracking al día.", link: "/profile/{user_id}?tab=wellness" },
  { name: "Rutina actualizada", type: "success", title: "Rutina actualizada", body: "El entrenador cargó una nueva rutina para vos. Revisala en tu perfil.", link: "/profile/{user_id}?tab=routines" },
  { name: "Plan nutricional nuevo", type: "success", title: "Nuevo plan nutricional", body: "Se actualizó tu plan nutricional. Chequeá los objetivos calóricos.", link: "/profile/{user_id}?tab=nutrition" },
  { name: "Testeo programado", type: "info", title: "Testeo físico esta semana", body: "Tenés un testeo programado. Descansá bien y hidratate el día previo.", link: "/profile/{user_id}?tab=tests" },
  { name: "Felicitaciones PR", type: "success", title: "Nuevo récord personal", body: "Superaste tu mejor marca. ¡Gran trabajo!", link: "/profile/{user_id}?tab=tests" },
  { name: "Revisar fatiga", type: "warning", title: "Atención: carga alta", body: "Tu carga de esta semana es elevada. Escuchá a tu cuerpo y descansá si lo necesitás.", link: "/profile/{user_id}?tab=wellness" },
];

const LINK_OPTIONS = [
  { value: "", label: "Sin link (solo notificación)" },
  { value: "/profile/{user_id}?tab=profile", label: "Perfil" },
  { value: "/profile/{user_id}?tab=tests", label: "Testeos físicos" },
  { value: "/profile/{user_id}?tab=routines", label: "Rutinas" },
  { value: "/profile/{user_id}?tab=nutrition", label: "Nutrición" },
  { value: "/profile/{user_id}?tab=wellness", label: "Wellness" },
  { value: "/profile/{user_id}?tab=documents", label: "Documentos" },
  { value: "/dashboard", label: "Dashboard" },
];

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [notifyUser, setNotifyUser] = useState(null);
  const [notif, setNotif] = useState({ title: "", body: "", type: "info", link: "" });
  const [sendingNotif, setSendingNotif] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcast, setBroadcast] = useState({ title: "", body: "", type: "info", link: "" });
  const [broadcasting, setBroadcasting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState({});
  const [paymentFilter, setPaymentFilter] = useState("all");

  const applyTemplate = (tpl, setter, current) => {
    setter({ ...current, title: tpl.title, body: tpl.body, type: tpl.type, link: tpl.link || "" });
  };

  // For single-user notification, swap {user_id} placeholder with the real targeted user id.
  const resolveLink = (link, userId) => (link && userId ? link.replace("{user_id}", userId) : link);

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcast.title.trim()) return;
    setBroadcasting(true);
    try {
      // Backend resolves {user_id} placeholder per recipient
      const { data } = await api.post("/admin/notifications/broadcast", broadcast);
      toast.success(`Notificación enviada a ${data.sent} atletas`);
      setBroadcastOpen(false);
      setBroadcast({ title: "", body: "", type: "info", link: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBroadcasting(false);
    }
  };

  const load = async () => {
    try {
      const [usersRes, payRes] = await Promise.all([
        api.get("/users"),
        api.get("/admin/payment-status").catch(() => ({ data: {} })),
      ]);
      setUsers(usersRes.data);
      setPaymentStatus(payRes.data || {});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setShowCreate(false);
      setForm({ email: "", password: "", name: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este usuario y todos sus datos?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleResetPassword = async (id, name) => {
    const pwd = window.prompt(`Nueva contraseña para ${name} (mínimo 6 caracteres):`);
    if (!pwd) return;
    if (pwd.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    try {
      await api.post(`/users/${id}/reset-password`, { new_password: pwd });
      toast.success(`Contraseña actualizada para ${name}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!notif.title.trim()) return;
    setSendingNotif(true);
    try {
      await api.post(`/users/${notifyUser.user_id}/notifications`, {
        title: notif.title,
        body: notif.body,
        type: notif.type,
        link: resolveLink(notif.link, notifyUser.user_id),
      });
      toast.success(`Notificación enviada a ${notifyUser.name}`);
      setNotifyUser(null);
      setNotif({ title: "", body: "", type: "info", link: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingNotif(false);
    }
  };

  return (
    <Layout>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-8">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-muted mb-2">Administración</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter uppercase">Usuarios</h1>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button onClick={() => setShowCreate(!showCreate)} className="btn-primary flex items-center gap-2" data-testid="new-user-btn">
            <UserPlus size={18} weight="bold" />
            Nuevo
          </button>
          <button onClick={() => setBroadcastOpen(true)} className="btn-secondary flex items-center gap-2" data-testid="broadcast-btn">
            <Megaphone size={18} weight="bold" />
            Notificar a todos
          </button>
        </div>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="card-base p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="new-user-form">
          <input className="input-base" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="new-user-name" />
          <input className="input-base" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="new-user-email" />
          <input className="input-base" type="password" placeholder="Contraseña" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="new-user-password" />
          <button type="submit" className="btn-primary flex items-center justify-center gap-2" data-testid="save-new-user">
            <Plus size={16} weight="bold" />Crear
          </button>
        </form>
      )}

      {!loading && users.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="payment-filter-bar">
          <span className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1 mr-1">
            <Receipt size={12} weight="bold" /> Estado de pago:
          </span>
          {[
            { k: "all", label: "Todos" },
            { k: "al_dia", label: "Al día" },
            { k: "advertencia", label: "Advertencia" },
            { k: "mora", label: "En mora" },
            { k: "sin_pagos", label: "Sin pagos" },
          ].map((o) => {
            const meta = PAYMENT_STATUS_META[o.k];
            const count = o.k === "all"
              ? users.length
              : users.filter((u) => (paymentStatus[u.user_id]?.status || "sin_pagos") === o.k).length;
            const active = paymentFilter === o.k;
            return (
              <button
                key={o.k}
                type="button"
                onClick={() => setPaymentFilter(o.k)}
                className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold border px-2.5 py-1.5 transition-colors ${
                  active ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-[#262626] text-muted hover:text-white"
                }`}
                data-testid={`pay-filter-${o.k}`}
              >
                {meta && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />}
                {o.label} <span className="text-[9px] opacity-70">· {count}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="users-loading">
          {[0,1,2,3].map((i) => (
            <div key={i} className="card-base p-4 flex items-center gap-4">
              <div className="w-9 h-9 bg-[#1F1F1F] animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-32 bg-[#1F1F1F] animate-pulse" />
                <div className="h-3 w-24 bg-[#1F1F1F] animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="card-base p-12 text-center fade-in-up">
          <UserPlus size={40} weight="duotone" className="text-[#FF3B30] mx-auto mb-4" />
          <div className="font-display font-black text-2xl uppercase tracking-tight mb-1">Sin atletas todavía</div>
          <div className="text-muted text-sm mb-6">Creá tu primer usuario para empezar a cargar rutinas, testeos y wellness.</div>
          <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="empty-create-user-btn">Crear primer usuario</button>
        </div>
      ) : (
        <div className="card-base overflow-hidden" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-app">
                <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-muted">Nombre</th>
                <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-muted hidden sm:table-cell">Email</th>
                <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-muted hidden lg:table-cell">Deporte</th>
                <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-muted">Pago</th>
                <th className="py-3 px-4 text-right text-xs uppercase tracking-widest text-muted">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users
                .filter((u) => paymentFilter === "all" || (paymentStatus[u.user_id]?.status || "sin_pagos") === paymentFilter)
                .map((u) => {
                  const ps = paymentStatus[u.user_id] || { status: "sin_pagos", days_since: null };
                  const meta = PAYMENT_STATUS_META[ps.status] || PAYMENT_STATUS_META.sin_pagos;
                  const tooltip = ps.last_payment
                    ? `Último pago: ${ps.last_payment} (hace ${ps.days_since} días)`
                    : "Nunca registró un pago";
                  return (
                <tr key={u.user_id} className="border-b border-app hover:bg-surface-hover transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-[#FF3B30] flex items-center justify-center font-display font-black overflow-hidden">
                        {u.profile?.avatar_url ? (
                          <img src={u.profile.avatar_url} alt={u.name} className="w-full h-full object-cover" />
                        ) : (
                          u.name?.[0]?.toUpperCase()
                        )}
                      </div>
                      <span className="font-semibold">{u.name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-muted hidden sm:table-cell">{u.email}</td>
                  <td className="py-4 px-4 text-muted hidden lg:table-cell">{u.profile?.sport || "-"}</td>
                  <td className="py-4 px-4">
                    <span
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold"
                      title={tooltip}
                      data-testid={`pay-status-${u.user_id}`}
                    >
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color, boxShadow: `0 0 8px ${meta.color}55` }} />
                      <span style={{ color: meta.color }}>{meta.label}</span>
                      {ps.days_since != null && ps.status !== "al_dia" && (
                        <span className="text-muted ml-1 normal-case tracking-normal">· {ps.days_since}d</span>
                      )}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => navigate(`/profile/${u.user_id}`)} className="btn-secondary !py-2 !px-3 flex items-center gap-1" data-testid={`open-user-${u.user_id}`}>
                        Abrir <ArrowRight size={14} weight="bold" />
                      </button>
                      <button onClick={() => { setNotifyUser(u); setNotif({ title: "", body: "", type: "info" }); }} className="btn-secondary !py-2 !px-3" title="Enviar notificación" data-testid={`notify-user-${u.user_id}`}>
                        <Bell size={14} weight="bold" />
                      </button>
                      <button onClick={() => handleResetPassword(u.user_id, u.name)} className="btn-secondary !py-2 !px-3" title="Resetear contraseña" data-testid={`reset-password-${u.user_id}`}>
                        <LockKey size={14} weight="bold" />
                      </button>
                      <button onClick={() => handleDelete(u.user_id)} className="btn-secondary !py-2 !px-3 hover:!border-[#FF3B30]" data-testid={`delete-user-${u.user_id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* Broadcast Modal */}
      {broadcastOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="broadcast-modal">
          <form onSubmit={handleBroadcast} className="card-base w-full max-w-lg p-6">
            <h3 className="font-display font-black uppercase text-2xl tracking-tighter mb-2">Notificar a todos</h3>
            <p className="text-muted text-sm mb-5">Se enviará a todos los atletas (usuarios no-admin).</p>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Plantilla rápida</label>
                <div className="flex gap-2 flex-wrap" data-testid="template-buttons">
                  {NOTIFICATION_TEMPLATES.map((tpl, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => applyTemplate(tpl, setBroadcast, broadcast)}
                      className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-[#262626] text-muted hover:text-white hover:border-white transition-colors"
                      data-testid={`template-${idx}`}
                    >
                      {tpl.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Tipo</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { k: "info", label: "Info", c: "#007AFF" },
                    { k: "success", label: "Éxito", c: "#32D74B" },
                    { k: "warning", label: "Aviso", c: "#FFD60A" },
                    { k: "critical", label: "Crítico", c: "#FF3B30" },
                  ].map((t) => (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setBroadcast({ ...broadcast, type: t.k })}
                      className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${
                        broadcast.type === t.k ? "text-white" : "text-muted border-[#262626] hover:text-white"
                      }`}
                      style={broadcast.type === t.k ? { backgroundColor: t.c, borderColor: t.c, color: t.k === "warning" ? "#000" : "#fff" } : {}}
                      data-testid={`broadcast-type-${t.k}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Título *</label>
                <input className="input-base" value={broadcast.title} onChange={(e) => setBroadcast({ ...broadcast, title: e.target.value })} required maxLength={80} data-testid="broadcast-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Mensaje</label>
                <textarea className="input-base" rows="3" value={broadcast.body} onChange={(e) => setBroadcast({ ...broadcast, body: e.target.value })} maxLength={300} data-testid="broadcast-body" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Al tocarla, abrir</label>
                <select
                  className="input-base"
                  value={broadcast.link}
                  onChange={(e) => setBroadcast({ ...broadcast, link: e.target.value })}
                  data-testid="broadcast-link"
                >
                  {LINK_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" disabled={broadcasting} className="btn-primary flex-1" data-testid="send-broadcast-btn">
                {broadcasting ? "Enviando..." : "Enviar a todos"}
              </button>
              <button type="button" onClick={() => setBroadcastOpen(false)} className="btn-secondary">Cancelar</button>
            </div>
          </form>
        </div>
      )}

      {/* Single User Notification Modal */}
      {notifyUser && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="notify-modal">
          <form onSubmit={handleSendNotification} className="card-base w-full max-w-lg p-6">
            <h3 className="font-display font-black uppercase text-2xl tracking-tighter mb-2">Notificar a {notifyUser.name}</h3>
            <p className="text-muted text-sm mb-5">Se enviará a su panel y, si tiene permisos activos, también como push.</p>
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Plantilla rápida</label>
                <div className="flex gap-2 flex-wrap" data-testid="user-template-buttons">
                  {NOTIFICATION_TEMPLATES.map((tpl, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => applyTemplate(tpl, setNotif, notif)}
                      className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-[#262626] text-muted hover:text-white hover:border-white transition-colors"
                      data-testid={`user-template-${idx}`}
                    >
                      {tpl.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Tipo</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { k: "info", label: "Info", c: "#007AFF" },
                    { k: "success", label: "Éxito", c: "#32D74B" },
                    { k: "warning", label: "Aviso", c: "#FFD60A" },
                    { k: "critical", label: "Crítico", c: "#FF3B30" },
                  ].map((t) => (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setNotif({ ...notif, type: t.k })}
                      className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${
                        notif.type === t.k ? "text-white" : "text-muted border-[#262626] hover:text-white"
                      }`}
                      style={notif.type === t.k ? { backgroundColor: t.c, borderColor: t.c, color: t.k === "warning" ? "#000" : "#fff" } : {}}
                      data-testid={`notif-type-${t.k}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Título *</label>
                <input className="input-base" value={notif.title} onChange={(e) => setNotif({ ...notif, title: e.target.value })} required maxLength={80} data-testid="notif-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Mensaje</label>
                <textarea className="input-base" rows="3" value={notif.body} onChange={(e) => setNotif({ ...notif, body: e.target.value })} maxLength={300} data-testid="notif-body" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-muted block mb-2">Al tocarla, abrir</label>
                <select
                  className="input-base"
                  value={notif.link}
                  onChange={(e) => setNotif({ ...notif, link: e.target.value })}
                  data-testid="notif-link"
                >
                  {LINK_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" disabled={sendingNotif} className="btn-primary flex-1" data-testid="send-notif-btn">
                {sendingNotif ? "Enviando..." : "Enviar notificación"}
              </button>
              <button type="button" onClick={() => setNotifyUser(null)} className="btn-secondary">Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
